import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PAYMENT_METHODS, CHARGE_PURPOSES } from "@/constants";
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { useRecordOfflinePaymentMutation } from "@/store/charges/apis";

interface RecordOfflinePaymentSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  moveId: string;
  onSuccess?: () => void;
}

export function RecordOfflinePaymentSheet({
  open,
  onOpenChange,
  moveId,
  onSuccess,
}: RecordOfflinePaymentSheetProps) {
  const [amount, setAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("");
  const [purpose, setPurpose] = useState("");
  const [issueReceipt, setIssueReceipt] = useState(false);
  const [recordOfflinePayment, { isLoading }] =
    useRecordOfflinePaymentMutation();
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      await recordOfflinePayment({
        moveId,
        amount: parseFloat(amount),
        payment_method: paymentMethod,
        purpose: purpose,
        send_receipt: issueReceipt,
      }).unwrap();

      toast({
        title: "Success",
        description: "Payment recorded successfully",
      });

      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      toast({
        title: "Error",
        description: (error as Error).message,
        variant: "destructive",
      });
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent>
        <SheetHeader>
          <SheetTitle>Record Offline Payment</SheetTitle>
        </SheetHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label htmlFor="amount">Amount</Label>
            <Input
              id="amount"
              type="number"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="purpose">Purpose</Label>
            <Select value={purpose} onValueChange={setPurpose} required>
              <SelectTrigger>
                <SelectValue placeholder="Select payment purpose" />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(CHARGE_PURPOSES).map(([key, value]) => (
                  <SelectItem key={key} value={key}>
                    {value}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="paymentMethod">Payment Method</Label>
            <Select
              value={paymentMethod}
              onValueChange={setPaymentMethod}
              required
            >
              <SelectTrigger>
                <SelectValue placeholder="Select payment method" />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(PAYMENT_METHODS).map(([key, value]) => (
                  <SelectItem key={key} value={key}>
                    {value}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox
              id="issueReceipt"
              checked={issueReceipt}
              onCheckedChange={(checked) => setIssueReceipt(checked as boolean)}
            />
            <Label htmlFor="issueReceipt">Issue Receipt</Label>
          </div>
          <Button type="submit" className="w-full" disabled={isLoading}>
            {isLoading ? "Recording..." : "Record Payment"}
          </Button>
        </form>
      </SheetContent>
    </Sheet>
  );
}
